import React, { useCallback, useEffect, useState } from 'react';
import MainLayout from '@shared/ui/main-layout';
import { reportsApi, downloadReportBlob } from '../../api/services/reports.service';
import {
  ReportPeriod,
  TicketReportData,
  OperatorReportRow,
  ClientReportData,
  ProductReportData,
} from '../../types/reportInterfaces';
import {
  TicketStatus,
  TicketPriority,
  TicketChannel,
  TICKET_STATUS_LABELS,
  TICKET_PRIORITY_LABELS,
  TICKET_CHANNEL_LABELS,
} from '../../types/ticketInterfaces';

type Tab = 'tickets' | 'operators' | 'clients' | 'products';

const TAB_LABELS: Record<Tab, string> = {
  tickets: 'Заявки',
  operators: 'Операторы',
  clients: 'Клиенты',
  products: 'Продукты',
};

const PERIOD_LABELS: Record<ReportPeriod, string> = {
  day: 'За сутки',
  week: 'За неделю',
  month: 'За месяц',
};

const fmtMin = (v: number | null): string => {
  if (v == null) return '—';
  if (v < 60) return `${v} мин`;
  return `${(v / 60).toFixed(1)} ч`;
};

const errText = (e: any): string => {
  if (e?.response?.status === 403) return 'Недостаточно прав для просмотра отчётов.';
  return e?.response?.data?.detail || 'Не удалось загрузить отчёт.';
};

const card = 'bg-white rounded-2xl shadow-[0_2px_12px_rgba(0,0,0,0.08)]';

const Reports: React.FC = () => {
  const [tab, setTab] = useState<Tab>('tickets');
  const [period, setPeriod] = useState<ReportPeriod>('month');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [ticketData, setTicketData] = useState<TicketReportData | null>(null);
  const [operatorData, setOperatorData] = useState<OperatorReportRow[]>([]);
  const [clientData, setClientData] = useState<ClientReportData | null>(null);
  const [productData, setProductData] = useState<ProductReportData | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      if (tab === 'tickets') {
        const { data } = await reportsApi.tickets({ period });
        setTicketData(data);
      } else if (tab === 'operators') {
        const { data } = await reportsApi.operators({ period });
        setOperatorData(data.operators);
      } else if (tab === 'products') {
        const { data } = await reportsApi.products({ period });
        setProductData(data);
      } else {
        const { data } = await reportsApi.clients({ period });
        setClientData(data);
      }
    } catch (e) {
      setError(errText(e));
    } finally {
      setLoading(false);
    }
  }, [tab, period]);

  useEffect(() => {
    load();
  }, [load]);

  const exportFile = async (format: 'csv' | 'xlsx') => {
    setError('');
    try {
      const fn =
        tab === 'operators'
          ? reportsApi.exportOperators
          : tab === 'products'
            ? reportsApi.exportProducts
            : reportsApi.exportTickets;
      const { data } = await fn(format, { period });
      downloadReportBlob(data, `${tab}_report.${format}`);
    } catch (e) {
      setError(errText(e));
    }
  };

  return (
    <MainLayout>
      <MainLayout.Title>Отчёты</MainLayout.Title>
      <MainLayout.Main>
        {/* ── Панель управления ── */}
        <div className="flex items-center gap-2 flex-wrap mb-5">
          {(Object.keys(TAB_LABELS) as Tab[]).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={[
                'px-4 h-[38px] text-sm font-semibold rounded-[10px] border transition-colors',
                tab === t
                  ? 'border-[#e3282a] text-[#e3282a] bg-[#e3282a]/5'
                  : 'border-[#D0D3D8] text-[#6B6F7B] hover:border-[#9DA7B5]',
              ].join(' ')}
            >
              {TAB_LABELS[t]}
            </button>
          ))}

          <div className="ml-auto flex items-center gap-2">
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value as ReportPeriod)}
              className="h-[38px] px-3 text-sm border border-[#D0D3D8] rounded-[10px] text-[#242426] bg-white cursor-pointer"
            >
              {(Object.keys(PERIOD_LABELS) as ReportPeriod[]).map((p) => (
                <option key={p} value={p}>
                  {PERIOD_LABELS[p]}
                </option>
              ))}
            </select>
            {tab !== 'clients' && (
              <>
                <button
                  type="button"
                  onClick={() => exportFile('csv')}
                  className="px-4 h-[38px] text-sm font-semibold text-[#242426] bg-white border border-[#d0d3d8] rounded-[10px] hover:bg-[#f0f2f4] transition-colors"
                >
                  CSV
                </button>
                <button
                  type="button"
                  onClick={() => exportFile('xlsx')}
                  className="px-4 h-[38px] text-sm font-semibold text-white bg-[#E3282A] rounded-[10px] hover:bg-[#C91F21] transition-colors"
                >
                  XLSX
                </button>
              </>
            )}
          </div>
        </div>

        {error && (
          <div className="mb-4 text-sm text-[#e3282a] bg-[#FFF5F5] border border-[#FFBDBD] rounded-[10px] px-4 py-3">
            {error}
          </div>
        )}

        {loading ? (
          <p className="text-[#bcbcbc] text-sm text-center py-10">Загрузка…</p>
        ) : (
          <>
            {tab === 'tickets' && ticketData && (
              <TicketsReport data={ticketData} />
            )}
            {tab === 'operators' && <OperatorsReport rows={operatorData} />}
            {tab === 'clients' && clientData && <ClientsReport data={clientData} />}
            {tab === 'products' && productData && <ProductsReport data={productData} />}
          </>
        )}
      </MainLayout.Main>
    </MainLayout>
  );
};

/* ══════════════ Заявки ══════════════ */

const Metric: React.FC<{ label: string; value: React.ReactNode; danger?: boolean }> = ({
  label,
  value,
  danger,
}) => (
  <div className={`${card} p-[18px_20px]`}>
    <p className="text-[#bcbcbc] text-sm mb-1.5">{label}</p>
    <p className={`text-2xl font-bold ${danger ? 'text-[#e3282a]' : 'text-[#242426]'}`}>{value}</p>
  </div>
);

const TicketsReport: React.FC<{ data: TicketReportData }> = ({ data }) => (
  <div className="flex flex-col gap-5">
    <div className="grid gap-4 grid-cols-[repeat(auto-fill,minmax(180px,1fr))]">
      <Metric label="Всего заявок" value={data.total} />
      <Metric label="Ср. время первого ответа" value={fmtMin(data.avg_first_response_min)} />
      <Metric label="Ср. время решения" value={fmtMin(data.avg_resolution_min)} />
      <Metric
        label="Нарушений SLA"
        value={`${data.sla_breached_count} (${data.sla_breached_pct}%)`}
        danger={data.sla_breached_count > 0}
      />
    </div>

    <div className="grid gap-5 grid-cols-1 md:grid-cols-2">
      <div className={`${card} p-[27px_31px]`}>
        <p className="text-[#242426] font-bold text-base mb-[15px]">По статусам</p>
        {(Object.keys(TICKET_STATUS_LABELS) as TicketStatus[]).map((s) => (
          <div key={s} className="flex justify-between text-sm py-1">
            <span className="text-[#bcbcbc]">{TICKET_STATUS_LABELS[s]}</span>
            <span className="font-semibold text-[#242426]">{data.by_status[s] ?? 0}</span>
          </div>
        ))}
      </div>
      <div className={`${card} p-[27px_31px]`}>
        <p className="text-[#242426] font-bold text-base mb-[15px]">По приоритетам</p>
        {(Object.keys(TICKET_PRIORITY_LABELS) as TicketPriority[]).map((p) => (
          <div key={p} className="flex justify-between text-sm py-1">
            <span className="text-[#bcbcbc]">{TICKET_PRIORITY_LABELS[p]}</span>
            <span className="font-semibold text-[#242426]">{data.by_priority[p] ?? 0}</span>
          </div>
        ))}
      </div>
    </div>
  </div>
);

/* ══════════════ Операторы ══════════════ */

const TH: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <th className="px-[14px] py-[9px] text-left text-[11px] text-[#999] font-medium uppercase tracking-wide whitespace-nowrap">
    {children}
  </th>
);
const TD: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <td className="px-[14px] py-[10px] text-sm text-[#555]">{children}</td>
);

const OperatorsReport: React.FC<{ rows: OperatorReportRow[] }> = ({ rows }) => (
  <div className={`${card} overflow-x-auto`}>
    <table className="w-full border-collapse text-sm">
      <thead>
        <tr className="bg-[#FAFAFA] border-b border-[#EBEBEB]">
          <TH>Оператор</TH>
          <TH>Принято</TH>
          <TH>Закрыто</TH>
          <TH>Ср. ответ</TH>
          <TH>Ср. решение</TH>
          <TH>Нарушений SLA</TH>
          <TH>% SLA</TH>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.operator_id} className="border-b border-[#F5F5F5] last:border-b-0">
            <TD>{r.operator_name}</TD>
            <TD>{r.total}</TD>
            <TD>{r.closed}</TD>
            <TD>{fmtMin(r.avg_first_response_min)}</TD>
            <TD>{fmtMin(r.avg_resolution_min)}</TD>
            <TD>{r.sla_breached}</TD>
            <TD>{r.sla_compliance_pct}%</TD>
          </tr>
        ))}
        {rows.length === 0 && (
          <tr>
            <td colSpan={7} className="text-center text-[#bcbcbc] text-sm py-10">
              Нет данных за период.
            </td>
          </tr>
        )}
      </tbody>
    </table>
  </div>
);

/* ══════════════ Клиенты ══════════════ */

const ClientsReport: React.FC<{ data: ClientReportData }> = ({ data }) => (
  <div className="flex flex-col gap-5">
    <div className="grid gap-4 grid-cols-[repeat(auto-fill,minmax(180px,1fr))]">
      <Metric label="Всего заявок" value={data.total} />
      {(Object.keys(TICKET_CHANNEL_LABELS) as TicketChannel[]).map((c) => (
        <Metric key={c} label={TICKET_CHANNEL_LABELS[c]} value={data.by_channel[c] ?? 0} />
      ))}
    </div>
    <div className={`${card} overflow-x-auto`}>
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="bg-[#FAFAFA] border-b border-[#EBEBEB]">
            <TH>Компания</TH>
            <TH>Всего</TH>
            <TH>Открытые</TH>
            <TH>Решённые</TH>
            <TH>Закрытые</TH>
          </tr>
        </thead>
        <tbody>
          {data.by_company.map((c) => (
            <tr key={c.company__id} className="border-b border-[#F5F5F5] last:border-b-0">
              <TD>{c.company__name}</TD>
              <TD>{c.total}</TD>
              <TD>{c.open}</TD>
              <TD>{c.solved}</TD>
              <TD>{c.closed}</TD>
            </tr>
          ))}
          {data.by_company.length === 0 && (
            <tr>
              <td colSpan={5} className="text-center text-[#bcbcbc] text-sm py-10">
                Нет данных за период.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  </div>
);

/* ══════════════ Клиент — Продукт — Проблема ══════════════ */

const dash = (v: string | null | undefined) => v || '—';

const ProductsReport: React.FC<{ data: ProductReportData }> = ({ data }) => (
  <div className="flex flex-col gap-5">
    <div className="grid gap-4 grid-cols-[repeat(auto-fill,minmax(180px,1fr))]">
      <Metric label="Всего заявок" value={data.total} />
      <Metric
        label="Без продукта"
        value={data.without_product}
        danger={data.without_product > 0}
      />
      <Metric
        label="Без типа проблемы"
        value={data.without_topic}
        danger={data.without_topic > 0}
      />
    </div>

    <div className="grid gap-5 grid-cols-1 md:grid-cols-2">
      <div className={`${card} p-[27px_31px]`}>
        <p className="text-[#242426] font-bold text-base mb-[15px]">По продуктам</p>
        {data.by_product.map((r) => (
          <div key={r.product__id ?? 'none'} className="flex justify-between text-sm py-1 gap-3">
            <span className="text-[#6B6F7B] truncate">{dash(r.product__name)}</span>
            <span className="font-semibold text-[#242426]">{r.total}</span>
          </div>
        ))}
        {data.by_product.length === 0 && (
          <p className="text-[#bcbcbc] text-sm">Нет данных за период.</p>
        )}
      </div>
      <div className={`${card} p-[27px_31px]`}>
        <p className="text-[#242426] font-bold text-base mb-[15px]">По типам проблем</p>
        {data.by_topic.map((r) => (
          <div key={r.topic__id ?? 'none'} className="flex justify-between text-sm py-1 gap-3">
            <span className="text-[#6B6F7B] truncate">
              {r.topic__group ? `${r.topic__group} / ` : ''}
              {dash(r.topic__name)}
            </span>
            <span className="font-semibold text-[#242426]">{r.total}</span>
          </div>
        ))}
        {data.by_topic.length === 0 && (
          <p className="text-[#bcbcbc] text-sm">Нет данных за период.</p>
        )}
      </div>
    </div>

    <div className={`${card} overflow-x-auto`}>
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="bg-[#FAFAFA] border-b border-[#EBEBEB]">
            <TH>Компания</TH>
            <TH>Продукт</TH>
            <TH>Тип проблемы</TH>
            <TH>Всего</TH>
            <TH>Открытые</TH>
            <TH>Решённые</TH>
            <TH>Закрытые</TH>
          </tr>
        </thead>
        <tbody>
          {data.matrix.map((r, i) => (
            <tr key={i} className="border-b border-[#F5F5F5] last:border-b-0">
              <TD>{dash(r.company__name)}</TD>
              <TD>{dash(r.product__name)}</TD>
              <TD>
                {r.topic__group ? `${r.topic__group} / ` : ''}
                {dash(r.topic__name)}
              </TD>
              <TD>{r.total}</TD>
              <TD>{r.open}</TD>
              <TD>{r.solved}</TD>
              <TD>{r.closed}</TD>
            </tr>
          ))}
          {data.matrix.length === 0 && (
            <tr>
              <td colSpan={7} className="text-center text-[#bcbcbc] text-sm py-10">
                Нет данных за период.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  </div>
);

export default Reports;
